import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { teamApi } from "@/lib/catalog-api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/catalog/Skeleton";
import { Wordmark } from "@/components/brand/Wordmark";

interface InviteInfo {
  email: string;
  role: string;
  workspace_name?: string;
  inviter_name?: string | null;
  user_exists?: boolean;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; info: InviteInfo }
  | { kind: "error"; status: number; message: string };

function classifyError(e: unknown): { status: number; message: string } {
  if (e instanceof ApiError) return { status: e.status, message: e.message };
  return { status: 0, message: "Could not load this invite." };
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, status, refreshMe } = useAuth();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setLoad({ kind: "error", status: 400, message: "No invite token in this link." });
      return;
    }
    teamApi.inviteInfo(token)
      .then((info) => setLoad({ kind: "ok", info }))
      .catch((e) => setLoad({ kind: "error", ...classifyError(e) }));
  }, [token]);

  async function acceptAsCurrentUser() {
    try {
      await teamApi.acceptInvite(token);
      await refreshMe();
      toast.success("You're in.");
      navigate("/app", { replace: true });
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not accept invite.");
    }
  }

  return (
    <main className="min-h-screen bg-background grid place-items-center px-6 py-16" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-xl tracking-tight inline-block mb-10">
          <Wordmark className="h-9" />
        </Link>

        {load.kind === "loading" ? (
          <Skeleton className="h-48" />
        ) : load.kind === "error" ? (
          <ErrorCard status={load.status} message={load.message} />
        ) : status === "loading" ? (
          <Skeleton className="h-48" />
        ) : !user ? (
          load.info.user_exists ? (
            <ExistingUserCard info={load.info} token={token} />
          ) : (
            <NewUserSignupCard info={load.info} token={token} />
          )
        ) : user.email.toLowerCase() === load.info.email.toLowerCase() ? (
          <ConfirmJoinCard info={load.info} onAccept={acceptAsCurrentUser} />
        ) : (
          <EmailMismatchCard info={load.info} currentEmail={user.email} />
        )}
      </div>
    </main>
  );
}

/* ---------- Cards ---------- */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-border bg-background p-8" data-testid="invite-card">
      {children}
    </div>
  );
}

function ErrorCard({ status, message }: { status: number; message: string }) {
  const heading =
    status === 410 ? "This invite has expired." :
    status === 404 ? "This invite no longer exists." :
    status === 403 ? "This invite isn't for you." :
    "We couldn't open this invite.";
  const sub =
    status === 410 ? "Ask the workspace owner to send a fresh link." :
    status === 404 ? "It may have been revoked or already accepted." :
    message;
  return (
    <div className="border border-border bg-bone/40 p-8" data-testid="invite-error">
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Invite</p>
      <h1 className="font-display text-2xl">{heading}</h1>
      <p className="mt-3 text-sm text-muted-foreground">{sub}</p>
      <Button asChild className="mt-6 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </div>
  );
}

function InviteHeader({ info }: { info: InviteInfo }) {
  return (
    <>
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" /> You're invited
      </p>
      <h1 className="font-display text-3xl">
        Join {info.workspace_name || "this workspace"}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {info.inviter_name ? <>{info.inviter_name} invited </> : "Invite for "}
        <span className="font-mono text-foreground">{info.email}</span> as a{" "}
        <span className="font-medium text-foreground">{info.role}</span>.
      </p>
    </>
  );
}

function NewUserSignupCard({ info, token }: { info: InviteInfo; token: string }) {
  const navigate = useNavigate();
  const { signupWithInvite } = useAuth();
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signupWithInvite({ token, full_name: fullName, password });
      toast.success(`Welcome to ${info.workspace_name}.`);
      navigate("/app", { replace: true });
    } catch (e: any) {
      const m = e instanceof ApiError ? e.message : "Could not create your account.";
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <InviteHeader info={info} />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-signup-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
          <Input value={info.email} readOnly disabled
            className="mt-1.5 h-10 rounded-none bg-bone/50"
            data-testid="invite-signup-email" />
        </div>
        <div>
          <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Full name
          </Label>
          <Input id="full_name" required value={fullName} onChange={(e: any) => setFullName(e.target.value)}
            data-testid="invite-signup-name"
            placeholder="Amelia Hart"
            className="mt-1.5 h-10 rounded-none" />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Password
          </Label>
          <Input id="password" type="password" required minLength={8}
            value={password} onChange={(e: any) => setPassword(e.target.value)}
            data-testid="invite-signup-password"
            placeholder="At least 8 characters"
            className="mt-1.5 h-10 rounded-none" />
        </div>
        {err && (
          <p className="text-sm text-oxblood" data-testid="invite-signup-error" role="alert">
            {err}
          </p>
        )}
        <Button type="submit" disabled={busy}
          data-testid="invite-signup-submit"
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
          {busy ? "Creating account…" : `Join ${info.workspace_name || "the workspace"}`}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Already have an account?{" "}
          <Link to={`/login?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(info.email)}`}
            className="text-oxblood hover:underline underline-offset-4">Sign in</Link>
        </p>
      </form>
    </Card>
  );
}

function ExistingUserCard({ info, token }: { info: InviteInfo; token: string }) {
  return (
    <Card>
      <InviteHeader info={info} />
      <p className="mt-6 text-sm text-foreground/80">
        An account already exists for <span className="font-mono">{info.email}</span>. Sign in to join.
      </p>
      <Button asChild data-testid="invite-existing-signin"
        className="mt-6 w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
        <Link to={`/login?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(info.email)}`}>
          Sign in to accept
        </Link>
      </Button>
    </Card>
  );
}

function ConfirmJoinCard({ info, onAccept }: { info: InviteInfo; onAccept: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <Card>
      <InviteHeader info={info} />
      <p className="mt-6 text-sm text-foreground/80 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-oxblood" />
        You'll keep your sign-in. Your current workspace stays — you'll switch to {info.workspace_name}.
      </p>
      <Button onClick={async () => { setBusy(true); try { await onAccept(); } finally { setBusy(false); } }}
        disabled={busy}
        data-testid="invite-accept-btn"
        className="mt-6 w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
        {busy ? "Accepting…" : `Join ${info.workspace_name || "the workspace"}`}
      </Button>
    </Card>
  );
}

function EmailMismatchCard({ info, currentEmail }: { info: InviteInfo; currentEmail: string }) {
  return (
    <Card>
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Email mismatch</p>
      <h1 className="font-display text-2xl">This invite isn't for this account.</h1>
      <p className="mt-3 text-sm text-foreground/80">
        You're signed in as <span className="font-mono">{currentEmail}</span>, but the invite was sent to{" "}
        <span className="font-mono">{info.email}</span>. Sign out and back in with that email to accept.
      </p>
      <Button asChild data-testid="invite-mismatch-signout"
        className="mt-6 w-full bg-foreground hover:bg-charcoal text-ivory rounded-none h-11">
        <Link to="/login">Sign in as a different user</Link>
      </Button>
    </Card>
  );
}
